import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { colors } from 'design-system';
import OptionPriceCardResume from 'src/components/screens/Quotations/components/OptionPriceCard/OptionPriceCardResume';
import { isPremiumOption } from 'src/functions/individualOptionHelpers';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import IndividualBenefits from './IndividualBenefits';
import IndividualCoverageResumeCard from './IndividualCoverageResumeCard';
import IndividualDeductibleResumeCard from './IndividualDeductibleResumeCard';

type IndividualUnitOptionProps = {
  currentProfile: PricingProfileResponse;
  quotation: Quotation;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  packageOption: number;
};

const useStyles = makeStyles(() => ({
  cardRoot: {
    position: 'relative',
    minWidth: 275,
    borderRadius: 10,
    width: '100%',
    overflow: 'hidden',
    backgroundColor: colors.white,
    boxShadow: ({ isMobile }: { isMobile: boolean }) =>
      !isMobile ? '0px 0px 10px 0px #0000000D' : '0px 5px 5px 0px #00000014',
    border: '1px solid #EAEAEA',
    maxWidth: ({ isMobile }: { isMobile: boolean }) =>
      isMobile ? '100%' : '500px',
    height: 'fit-content',
    '& .MuiAccordion-root': {
      boxShadow: 'none',
      border: 'none',
    },
    '& .MuiAccordionSummary-root': {
      padding: '0px',
      display: 'flex',
      alignItems: 'flex-start',
    },
    '& .MuiAccordionSummary-content': {
      justifyContent: 'center',
      margin: '0px',
    },
    '& .MuiAccordionDetails-root': {
      padding: '0px',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '1.5rem',
  },
  triangle: {
    position: 'absolute',
    borderTop: '48.45px solid #E37E07',
    borderRight: '55.45px solid transparent',
    zIndex: 1,
  },
  star: {
    position: 'absolute',
    top: '5px',
    left: '4px',
    zIndex: 2,
  },
  divider: {
    width: '100%',
    borderTop: 'solid 1px #000000',
    opacity: '0.12',
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
  },
}));

const ExtraInfo = ({
  quotation,
  coverages,
  coverageResumes,
  unit,
  classes,
  currentProfile,
  packageOption,
  premiumOption,
  children,
}: {
  quotation: Quotation;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  unit: QuotationUnit;
  classes: ClassNameMap;
  currentProfile: PricingProfileResponse;
  packageOption: number;
  premiumOption: boolean;
  children?: JSX.Element;
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ gap: '1rem' }}
      width="100%"
    >
      {children}
      <hr className={classes.divider} />
      <IndividualCoverageResumeCard
        data-testid="individual-coverages-card"
        quotation={quotation}
        coverageCatalogs={coverages}
        coverageCatalogResumes={coverageResumes}
        unit={unit}
      />
      <hr className={classes.divider} />
      <IndividualDeductibleResumeCard
        data-testid="individual-deductibles-card"
        coverageCatalogs={coverages}
        coverageCatalogResumes={coverageResumes}
        unit={unit}
        moneyType={currentProfile.typeOfMoney}
        packageOption={packageOption}
        createdAt={quotation.createdAt}
      />
      <hr className={classes.divider} />
      <IndividualBenefits
        data-testid="individual-endorsement-card"
        isPremiumOption={premiumOption}
        coverageCatalogs={coverages}
        packageCoverages={unit.coverages}
        currency={currentProfile.typeOfMoney}
        currentProfile={currentProfile}
      />
    </Box>
  );
};

export const IndividualUnitOption = ({
  currentProfile,
  quotation,
  coverages,
  coverageResumes,
  packageOption,
}: IndividualUnitOptionProps): JSX.Element => {
  const anualPrice = quotation.units[packageOption].total.grandTotal;
  const unit = quotation.units[packageOption];
  const premiumOption = isPremiumOption(quotation.units, packageOption);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles({ isMobile });

  return (
    <Box className={classes.cardRoot}>
      {premiumOption && (
        <>
          <Box className={classes.triangle} />
          <img
            src="/svg/premium_start.svg"
            alt="premium star"
            className={classes.star}
          />
        </>
      )}
      <Box className={classes.container}>
        {isMobile ? (
          <Accordion>
            <AccordionSummary
              data-testid="accordion-summary"
              expandIcon={
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  color={colors.bright_turquoise}
                />
              }
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <OptionPriceCardResume
                data-testid="option-price-card"
                anualPrice={anualPrice}
                moneyType={currentProfile.typeOfMoney}
                createdAt={quotation.createdAt}
                unit={unit}
              />
            </AccordionSummary>
            <AccordionDetails data-testid="accordion-details">
              <ExtraInfo
                quotation={quotation}
                coverages={coverages}
                coverageResumes={coverageResumes}
                unit={unit}
                classes={classes}
                currentProfile={currentProfile}
                packageOption={packageOption}
                premiumOption={premiumOption}
              />
            </AccordionDetails>
          </Accordion>
        ) : (
          <ExtraInfo
            quotation={quotation}
            coverages={coverages}
            coverageResumes={coverageResumes}
            unit={unit}
            classes={classes}
            currentProfile={currentProfile}
            packageOption={packageOption}
            premiumOption={premiumOption}
          >
            <OptionPriceCardResume
              data-testid="option-price-card"
              anualPrice={anualPrice}
              moneyType={currentProfile.typeOfMoney}
              createdAt={quotation.createdAt}
              unit={unit}
            />
          </ExtraInfo>
        )}
      </Box>
    </Box>
  );
};
export default IndividualUnitOption;
